import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  TrendingUp as SalesIcon,
  Percent as DiscountIcon,
  Star as VIPIcon,
  LocalOffer as TagIcon,
  Business as BusinessIcon,
  Store as RetailIcon,
  Shuffle as AutoIcon,
  PersonAdd as AddMemberIcon,
  Info as InfoIcon,
  CheckCircle as ActiveIcon,
  Block as InactiveIcon,
} from "@mui/icons-material";

const CustomerGroups = () => {
  // Sample customer groups data
  const [groups, setGroups] = useState([
    {
      id: 1,
      groupCode: "GRP001",
      groupName: "VIP Customers",
      groupType: "VIP",
      description: "High-value customers with premium benefits",
      memberCount: 25,
      totalSales: 2750000,
      avgPurchaseValue: 110000,
      discountPercentage: 15,
      priceListType: "VIP Pricing",
      autoAssignment: true,
      assignmentCriteria: "Total purchases > ₹500,000",
      status: "Active",
      createdDate: "2023-05-15",
      color: "#9c27b0",
    },
    {
      id: 2,
      groupCode: "GRP002",
      groupName: "Wholesale Partners",
      groupType: "Wholesale",
      description: "Bulk buyers and resellers",
      memberCount: 48,
      totalSales: 8500000,
      avgPurchaseValue: 177083,
      discountPercentage: 20,
      priceListType: "Wholesale",
      autoAssignment: true,
      assignmentCriteria: "Customer type = Wholesale",
      status: "Active",
      createdDate: "2023-04-10",
      color: "#1976d2",
    },
    {
      id: 3,
      groupCode: "GRP003",
      groupName: "Regular Customers",
      groupType: "Regular",
      description: "Standard retail customers",
      memberCount: 156,
      totalSales: 4680000,
      avgPurchaseValue: 30000,
      discountPercentage: 5,
      priceListType: "Standard",
      autoAssignment: true,
      assignmentCriteria: "Customer type = Retail",
      status: "Active",
      createdDate: "2023-03-20",
      color: "#2e7d32",
    },
    {
      id: 4,
      groupCode: "GRP004",
      groupName: "New Customers",
      groupType: "Promotional",
      description: "First-time buyers - special onboarding offers",
      memberCount: 42,
      totalSales: 840000,
      avgPurchaseValue: 20000,
      discountPercentage: 10,
      priceListType: "Promotional",
      autoAssignment: true,
      assignmentCriteria: "Registration within last 30 days",
      status: "Active",
      createdDate: "2023-06-01",
      color: "#ff9800",
    },
    {
      id: 5,
      groupCode: "GRP005",
      groupName: "Inactive Customers",
      groupType: "Re-engagement",
      description: "Customers with no purchase in last 90 days",
      memberCount: 28,
      totalSales: 420000,
      avgPurchaseValue: 15000,
      discountPercentage: 12,
      priceListType: "Win-back",
      autoAssignment: true,
      assignmentCriteria: "No purchase in last 90 days",
      status: "Active",
      createdDate: "2023-07-12",
      color: "#d32f2f",
    },
  ]);

  // Sample customers for assignment
  const [availableCustomers] = useState([
    { id: 1, name: "Rajesh Kumar", purchases: 245000, type: "Retail" },
    { id: 2, name: "Priya Sharma", purchases: 550000, type: "VIP" },
    { id: 3, name: "Amit Patel", purchases: 1250000, type: "Wholesale" },
    { id: 4, name: "Sneha Desai", purchases: 125000, type: "Retail" },
    { id: 5, name: "Vikram Singh", purchases: 980000, type: "Wholesale" },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openMembersModal, setOpenMembersModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [groupForm, setGroupForm] = useState({
    groupCode: "",
    groupName: "",
    groupType: "Regular",
    description: "",
    discountPercentage: 0,
    priceListType: "Standard",
    autoAssignment: false,
    assignmentType: "Manual",
    criteriaField: "",
    criteriaOperator: "",
    criteriaValue: "",
    status: "Active",
    color: "#1976d2",
  });

  // Helper functions
  const getFilteredGroups = () => {
    return groups.filter((group) => {
      const matchesSearch = Object.values(group).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || group.status === statusFilter;
      const matchesType = typeFilter === "all" || group.groupType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'VIP': return 'secondary';
      case 'Wholesale': return 'primary';
      case 'Regular': return 'success';
      case 'Promotional': return 'warning';
      case 'Re-engagement': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handlers
  const handleEdit = (group) => {
    setEditingGroup(group);
    setGroupForm({
      groupCode: group.groupCode,
      groupName: group.groupName,
      groupType: group.groupType,
      description: group.description,
      discountPercentage: group.discountPercentage,
      priceListType: group.priceListType,
      autoAssignment: group.autoAssignment,
      assignmentType: group.autoAssignment ? "Auto" : "Manual",
      criteriaField: "",
      criteriaOperator: "",
      criteriaValue: "",
      status: group.status,
      color: group.color,
    });
    setOpenModal(true);
  };

  const handleView = (group) => {
    setViewingGroup(group);
    setOpenViewModal(true);
  };

  const handleManageMembers = (group) => {
    setViewingGroup(group);
    setSelectedMembers([]);
    setOpenMembersModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      setGroups(groups.filter((group) => group.id !== id));
      setSuccessMessage("Group deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredGroups().length} groups.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingGroup(null);
    const newGroupCode = `GRP${String(groups.length + 1).padStart(3, '0')}`;
    setGroupForm({
      groupCode: newGroupCode,
      groupName: "",
      groupType: "Regular",
      description: "",
      discountPercentage: 0,
      priceListType: "Standard",
      autoAssignment: false,
      assignmentType: "Manual",
      criteriaField: "",
      criteriaOperator: "",
      criteriaValue: "",
      status: "Active",
      color: "#1976d2",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingGroup(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingGroup(null);
  };

  const handleCloseMembersModal = () => {
    setOpenMembersModal(false);
    setViewingGroup(null);
    setSelectedMembers([]);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGroupForm({ 
      ...groupForm, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleMemberToggle = (customerId) => {
    setSelectedMembers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSaveGroup = () => {
    // Validation
    if (!groupForm.groupName) {
      setErrors({ groupName: "Group name is required" });
      return;
    }

    const newGroup = {
      id: editingGroup ? editingGroup.id : groups.length + 1,
      groupCode: groupForm.groupCode,
      groupName: groupForm.groupName,
      groupType: groupForm.groupType,
      description: groupForm.description,
      memberCount: editingGroup ? editingGroup.memberCount : 0,
      totalSales: editingGroup ? editingGroup.totalSales : 0,
      avgPurchaseValue: editingGroup ? editingGroup.avgPurchaseValue : 0,
      discountPercentage: parseFloat(groupForm.discountPercentage) || 0,
      priceListType: groupForm.priceListType,
      autoAssignment: groupForm.autoAssignment,
      assignmentCriteria: groupForm.autoAssignment ? `${groupForm.criteriaField} ${groupForm.criteriaOperator} ${groupForm.criteriaValue}` : "Manual",
      status: groupForm.status,
      createdDate: editingGroup ? editingGroup.createdDate : new Date().toISOString().split('T')[0],
      color: groupForm.color,
    };

    if (editingGroup) {
      setGroups(groups.map(g => g.id === editingGroup.id ? newGroup : g));
      setSuccessMessage("Group updated successfully!");
    } else {
      setGroups([newGroup, ...groups]);
      setSuccessMessage("Group created successfully!");
    }

    handleCloseModal();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleAddMembers = () => {
    setSuccessMessage(`${selectedMembers.length} customers added to ${viewingGroup.groupName}!`);
    handleCloseMembersModal();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const filteredGroups = getFilteredGroups();

  // Calculate statistics
  const totalGroups = groups.length;
  const activeGroups = groups.filter(g => g.status === "Active").length;
  const totalMembers = groups.reduce((sum, g) => sum + g.memberCount, 0);
  const totalGroupSales = groups.reduce((sum, g) => sum + g.totalSales, 0);

  // Get unique values for filters
  const groupTypes = [...new Set(groups.map(g => g.groupType))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Customer Groups
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Segment customers for targeted marketing and pricing • {filteredGroups.length} groups found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Create New Group
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Groups</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalGroups}
                  </Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active Groups</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {activeGroups}
                  </Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Members</Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {totalMembers}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Group Sales</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {formatCurrency(totalGroupSales)}
                  </Typography>
                </Box>
                <SalesIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Groups..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Group Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Types</option>
              {groupTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Group Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Group Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Total Sales</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Avg Purchase</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Discount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Assignment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.length > 0 ? (
                filteredGroups
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((group) => (
                    <TableRow 
                      key={group.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              backgroundColor: group.color 
                            }} 
                          />
                          <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                            {group.groupCode}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {group.groupName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={group.groupType} 
                          size="small" 
                          color={getTypeColor(group.groupType)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="600">
                            {group.memberCount}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                          {formatCurrency(group.totalSales)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(group.avgPurchaseValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={<DiscountIcon />}
                          label={`${group.discountPercentage}%`}
                          size="small" 
                          sx={{ 
                            bgcolor: 'success.light', 
                            color: 'success.dark',
                            fontWeight: 600 
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {group.autoAssignment ? (
                          <Chip 
                            icon={<AutoIcon />}
                            label="Auto" 
                            size="small" 
                            color="info"
                          />
                        ) : (
                          <Chip 
                            label="Manual" 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={group.status}
                          size="small"
                          sx={{
                            backgroundColor: group.status === 'Active' ? '#e8f5e9' : '#ffebee',
                            color: group.status === 'Active' ? '#2e7d32' : '#d32f2f',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(group)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Manage Members">
                            <IconButton
                              size="small"
                              onClick={() => handleManageMembers(group)}
                              sx={{
                                color: 'secondary.main',
                                '&:hover': { backgroundColor: 'secondary.light' },
                              }}
                            >
                              <AddMemberIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(group)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(group.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No groups found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredGroups.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Group Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon />
            {editingGroup ? 'Edit Customer Group' : 'Create New Customer Group'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Group Code"
                name="groupCode"
                value={groupForm.groupCode}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TagIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Group Type *</InputLabel>
                <Select
                  name="groupType"
                  value={groupForm.groupType}
                  onChange={handleFormChange}
                  label="Group Type *"
                >
                  <MenuItem value="VIP">VIP</MenuItem>
                  <MenuItem value="Wholesale">Wholesale</MenuItem>
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Promotional">Promotional</MenuItem>
                  <MenuItem value="Re-engagement">Re-engagement</MenuItem>
                  <MenuItem value="Corporate">Corporate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name *"
                name="groupName"
                value={groupForm.groupName}
                onChange={handleFormChange}
                error={!!errors.groupName}
                helperText={errors.groupName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GroupIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={groupForm.description}
                onChange={handleFormChange}
                multiline
                rows={2}
                placeholder="Describe the purpose and criteria for this group..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Percentage"
                name="discountPercentage"
                type="number"
                value={groupForm.discountPercentage}
                onChange={handleFormChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DiscountIcon />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Price List Type</InputLabel>
                <Select
                  name="priceListType"
                  value={groupForm.priceListType}
                  onChange={handleFormChange}
                  label="Price List Type"
                >
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Wholesale">Wholesale</MenuItem>
                  <MenuItem value="VIP Pricing">VIP Pricing</MenuItem>
                  <MenuItem value="Promotional">Promotional</MenuItem>
                  <MenuItem value="Win-back">Win-back</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Group Color"
                name="color"
                type="color"
                value={groupForm.color}
                onChange={handleFormChange}
                helperText="Visual identifier for the group"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={groupForm.status}
                  onChange={handleFormChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <FormLabel component="legend">Customer Assignment</FormLabel>
                <RadioGroup
                  name="assignmentType"
                  value={groupForm.assignmentType}
                  onChange={(e) => {
                    handleFormChange(e);
                    setGroupForm({
                      ...groupForm,
                      assignmentType: e.target.value,
                      autoAssignment: e.target.value === 'Auto',
                    });
                  }}
                  row
                >
                  <FormControlLabel value="Manual" control={<Radio />} label="Manual Assignment" />
                  <FormControlLabel value="Auto" control={<Radio />} label="Auto Assignment (Rule-based)" />
                </RadioGroup>

                {groupForm.assignmentType === 'Auto' && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Criteria Field</InputLabel>
                      <Select
                        name="criteriaField"
                        value={groupForm.criteriaField}
                        onChange={handleFormChange}
                        label="Criteria Field"
                        size="small"
                      >
                        <MenuItem value="Total Purchases">Total Purchases</MenuItem>
                        <MenuItem value="Customer Type">Customer Type</MenuItem>
                        <MenuItem value="Registration Date">Registration Date</MenuItem>
                        <MenuItem value="Last Purchase">Last Purchase</MenuItem>
                        <MenuItem value="Loyalty Points">Loyalty Points</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        name="criteriaOperator"
                        value={groupForm.criteriaOperator}
                        onChange={handleFormChange}
                        label="Operator"
                        size="small"
                      >
                        <MenuItem value=">">Greater than</MenuItem>
                        <MenuItem value="<">Less than</MenuItem>
                        <MenuItem value="=">Equals</MenuItem>
                        <MenuItem value=">=">Greater or Equal</MenuItem>
                        <MenuItem value="<=">Less or Equal</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ flex: 1 }}
                      label="Value"
                      name="criteriaValue"
                      value={groupForm.criteriaValue}
                      onChange={handleFormChange}
                      size="small"
                      placeholder="e.g., 500000"
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveGroup}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {editingGroup ? 'Update Group' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Group Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Group Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingGroup && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '50%', 
                      backgroundColor: viewingGroup.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <GroupIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {viewingGroup.groupName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip 
                        label={viewingGroup.groupType} 
                        color={getTypeColor(viewingGroup.groupType)}
                        size="small"
                      />
                      <Chip 
                        label={viewingGroup.status}
                        sx={{
                          backgroundColor: viewingGroup.status === 'Active' ? '#e8f5e9' : '#ffebee',
                          color: viewingGroup.status === 'Active' ? '#2e7d32' : '#d32f2f',
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {viewingGroup.description}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Group Code</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingGroup.groupCode}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingGroup.createdDate}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(156, 39, 176, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Members</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }} color="secondary.main">
                    {viewingGroup.memberCount}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Sales</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="success.main">
                    {formatCurrency(viewingGroup.totalSales)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Average Purchase Value</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="primary.main">
                    {formatCurrency(viewingGroup.avgPurchaseValue)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Discount Percentage</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="warning.main">
                    {viewingGroup.discountPercentage}%
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Price List Type</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingGroup.priceListType}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Assignment Method</Typography>
                  <Box sx={{ mt: 1 }}>
                    {viewingGroup.autoAssignment ? (
                      <Chip icon={<AutoIcon />} label="Auto Assignment" color="info" />
                    ) : (
                      <Chip label="Manual Assignment" variant="outlined" />
                    )}
                  </Box>
                </Paper>
              </Grid>

              {viewingGroup.autoAssignment && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                    <Typography variant="caption" color="text.secondary">Assignment Criteria</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                      {viewingGroup.assignmentCriteria}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
          {viewingGroup && (
            <Button
              variant="contained"
              onClick={() => {
                handleCloseViewModal();
                handleEdit(viewingGroup);
              }}
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Edit Group
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Manage Members Modal */}
      <Dialog open={openMembersModal} onClose={handleCloseMembersModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddMemberIcon />
            Add Members to {viewingGroup?.groupName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          <TextField
            fullWidth
            placeholder="Search customers..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select customers to add to this group
          </Typography>
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {availableCustomers.map((customer) => (
              <ListItem 
                key={customer.id}
                sx={{ 
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedMembers.includes(customer.id)}
                    onChange={() => handleMemberToggle(customer.id)}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={customer.name}
                  secondary={`${customer.type} • ${formatCurrency(customer.purchases)}`}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected: {selectedMembers.length} customer(s)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseMembersModal}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddMembers}
            disabled={selectedMembers.length === 0}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Add {selectedMembers.length} Member(s)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerGroups;

